import React, { useCallback, useEffect, useState } from "react";
import { Send, RefreshCw, Loader2, MailOpen, MousePointerClick, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : null);

export default function CampaignPanel() {
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/campaigns/abandoned-cart/stats").then(({ data }) => setData(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, [load]);

  const sendCampaign = async () => {
    setSending(true);
    setResult(null);
    try {
      const { data: res } = await api.post("/admin/campaigns/abandoned-cart/send");
      setResult(`Campagne lancée : ${res.recipients} client(s) ciblé(s) sur ${res.pending_orders} commandes en attente` +
        (res.skipped?.length ? ` — ${res.skipped.length} exclu(s) (contactés récemment)` : ""));
      setConfirm(false);
      setTimeout(load, 2000);
    } catch (e) {
      setResult(`Erreur : ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSending(false);
    }
  };

  const s = data?.summary;
  const sendingNow = data?.campaigns?.[0]?.status === "sending";

  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid="campaign-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-heading text-base font-semibold">📬 Relance paniers abandonnés</h3>
          <p className="text-xs text-muted-foreground">Email tracké (ouverture + clic) à tous les clients avec commande en attente de paiement. Actualisation auto 30 s.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="campaign-refresh"><RefreshCw className="mr-1 h-3.5 w-3.5" />Actualiser</Button>
          {!confirm ? (
            <Button size="sm" onClick={() => setConfirm(true)} disabled={sendingNow} data-testid="campaign-send-btn">
              <Send className="mr-1 h-3.5 w-3.5" />{sendingNow ? "Envoi en cours..." : "Envoyer la relance"}
            </Button>
          ) : (
            <Button size="sm" variant="destructive" onClick={sendCampaign} disabled={sending} data-testid="campaign-send-confirm">
              {sending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Send className="mr-1 h-3.5 w-3.5" />}
              Confirmer l'envoi réel
            </Button>
          )}
        </div>
      </div>
      {result && <p className="mt-2 rounded-xl bg-primary/10 px-3 py-2 text-xs" data-testid="campaign-result">{result}</p>}

      {s && (
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="rounded-xl border border-border p-3">
            <p className="flex items-center gap-1 text-[11px] text-muted-foreground"><Mail className="h-3 w-3" />Envoyés</p>
            <p className="font-heading text-lg font-semibold" data-testid="campaign-sent">{s.sent}{s.queued > 0 && <span className="ml-1 text-xs text-muted-foreground">(+{s.queued} en file)</span>}</p>
          </div>
          <div className="rounded-xl border border-border p-3">
            <p className="flex items-center gap-1 text-[11px] text-muted-foreground"><MailOpen className="h-3 w-3" />Ouverts</p>
            <p className="font-heading text-lg font-semibold text-success" data-testid="campaign-opened">{s.opened} <span className="text-xs text-muted-foreground">({s.open_rate}%)</span></p>
          </div>
          <div className="rounded-xl border border-border p-3">
            <p className="flex items-center gap-1 text-[11px] text-muted-foreground"><MousePointerClick className="h-3 w-3" />Cliqués</p>
            <p className="font-heading text-lg font-semibold text-primary" data-testid="campaign-clicked">{s.clicked} <span className="text-xs text-muted-foreground">({s.click_rate}%)</span></p>
          </div>
          <div className="rounded-xl border border-border p-3">
            <p className="text-[11px] text-muted-foreground">Échecs d'envoi</p>
            <p className="font-heading text-lg font-semibold" data-testid="campaign-failed">{s.failed}</p>
          </div>
        </div>
      )}

      {data?.recipients?.length > 0 && (
        <div className="mt-3 overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-left text-xs">
            <thead className="bg-muted/50 text-[10px] uppercase text-muted-foreground">
              <tr>
                <th className="p-2">Client</th><th className="p-2">Commandes</th><th className="p-2">Jetons</th>
                <th className="p-2">Envoyé</th><th className="p-2">Ouvert</th><th className="p-2">Cliqué</th><th className="p-2">Statut</th>
              </tr>
            </thead>
            <tbody>
              {data.recipients.map((r) => (
                <tr key={r.tracking_id} className="border-t border-border" data-testid={`campaign-row-${r.tracking_id}`}>
                  <td className="max-w-[180px] truncate p-2">{r.email}</td>
                  <td className="p-2">{r.orders_count}</td>
                  <td className="p-2 font-semibold">{r.total_tokens}</td>
                  <td className="whitespace-nowrap p-2 text-muted-foreground">{fmt(r.sent_at) || "—"}</td>
                  <td className="whitespace-nowrap p-2">{r.opened_at ? <span className="text-success">✓ {fmt(r.last_opened_at || r.opened_at)}{r.opens > 1 ? ` ×${r.opens}` : ""}</span> : "—"}</td>
                  <td className="whitespace-nowrap p-2">{r.clicked_at ? <span className="text-primary">✓ {fmt(r.clicked_at)}{r.clicks > 1 ? ` ×${r.clicks}` : ""}</span> : "—"}</td>
                  <td className="p-2">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                      r.clicked_at ? "bg-primary/15 text-primary"
                        : r.opened_at ? "bg-success/15 text-success"
                        : r.status === "sent" ? "bg-muted text-muted-foreground"
                        : r.status === "failed" ? "bg-destructive/15 text-destructive" : "bg-amber-500/15 text-amber-600"
                    }`}>
                      {r.clicked_at ? "A cliqué" : r.opened_at ? "A ouvert" : r.status === "sent" ? "Envoyé" : r.status === "failed" ? "Échec" : "En file"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && !data.recipients?.length && <p className="mt-3 text-xs text-muted-foreground">Aucune campagne envoyée pour le moment.</p>}
    </div>
  );
}
